import { useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import {
  Box,
  Checkbox,
  CircularProgress,
  Collapse,
  Divider,
  Fade,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Form, Formik } from "formik";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { v4 } from "uuid";
import * as yup from "yup";

import type { ShareableIncidentDataV1Response } from "@thalamos/common";
import { dayjs } from "@thalamos/common";
import type {
  AcceptVisionIncidentResponse,
  IncidentPreview136,
  PreviewVisionIncidentReponse,
  VisionAcceptIncidentRequest,
} from "@thalamos/aspire-common";
import {
  isGuestUserSession,
  nonEmptyRequiredString,
} from "@thalamos/aspire-common";

import { api, apiHooks, logout } from "~/api.js";
import {
  AddressField,
  Banner,
  BannerList,
  Button,
  FormTitle,
  PopupDialog,
  renderErrorToast,
  renderSuccessToast,
  TextField,
  Typeahead,
} from "~/components/design-system/index.js";
import { FormFooterSection } from "~/components/form/FormFooter.js";
import type { SelectedTrust } from "~/components/form/TrustPicker.js";
import { TrustPicker } from "~/components/form/TrustPicker.js";
import { PatientBanner } from "~/components/layout/index.js";
import { MultiPagePdf } from "~/components/MultiPagePdf.js";
import { queueS132Prompt } from "~/components/s132/s132PromptQueue.js";
import { config } from "~/config.js";
import { LoggedInUserContext } from "~/Contexts.js";
import { routeFns } from "~/routePaths.js";
import { logActionToDatadog } from "~/tracing.js";
import { ukLocalFormatDate } from "~/util.js";

export type VisionIncidentPreviewPageProps = {};

export type VisionIncidentPreviewS136FormApi = {
  acceptIncident: (data: VisionAcceptIncidentRequest) => Promise<{
    status: number;
    data: AcceptVisionIncidentResponse;
  }>;
  rejectIncident: (
    incidentId: string,
    reason: string,
  ) => Promise<{ status: number }>;
};

export type VisionIncidentPreviewS136FormProps = {
  incidentPreviewData: IncidentPreview136;
  incidentId: string;
  onAccepted: (formContextId: string, patientId: string) => void;
  onRejected: () => void;
  isGuestUser?: boolean;
  onLogout?: () => void;
  apiOverride?: VisionIncidentPreviewS136FormApi;
};

/**
 * Wraps a conditionally-rendered form section so it animates into view rather
 * than popping in. The section expands its height (Collapse) while fading in
 * (Fade), so content below slides down smoothly. Honours the OS
 * "reduce motion" preference by running instantly for those users.
 *
 * Any divider that visually precedes a section belongs *inside* the reveal so
 * it appears and disappears with the section, never leaving a dangling line.
 */
const RevealSection = ({
  in: inProp,
  children,
}: {
  in: boolean;
  children: ReactNode;
}) => {
  const prefersReducedMotion = useMediaQuery(
    "(prefers-reduced-motion: reduce)",
  );
  const theme = useTheme();
  // A single symmetric curve for both directions. Several sibling sections are
  // gated on coupled conditions, so one can collapse while another expands at
  // the same moment; matching their easing keeps them moving in lockstep rather
  // than reading as two different motions.
  const easing = theme.transitions.easing.easeInOut;
  // One duration for enter and exit, for the same reason — an asymmetric
  // enter/exit made concurrent expand/collapse change speed mid-motion, which
  // showed up as a "bounce" in the content below. Instant (no animation) when
  // the user prefers reduced motion.
  const timeout = prefersReducedMotion
    ? 0
    : theme.transitions.duration.standard;
  return (
    <Collapse in={inProp} timeout={timeout} easing={easing} unmountOnExit>
      <Fade in={inProp} timeout={timeout} easing={easing}>
        {/* Flex column so children stack vertically, matching the outer Stack
            they were lifted out of — MUI FormControl/FormControlLabel are
            inline-flex and would otherwise flow side by side in a plain Box. */}
        <Box sx={{ display: "flex", flexDirection: "column" }}>{children}</Box>
      </Fade>
    </Collapse>
  );
};

const ProfileCard = ({
  patientDetails,
}: {
  patientDetails: ShareableIncidentDataV1Response["patientDetails"];
}) => {
  const { t } = useTranslation();
  return (
    <>
      <Grid
        container
        spacing={1}
        sx={{
          alignItems: "center",
        }}
      >
        <Grid size={3}>
          <Typography variant="body2" color="textSecondary">
            {t("pages.incidentPreviewPage.name")}
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              fontWeight: "bold",
            }}
          >
            {patientDetails.name.given} {patientDetails.name.family}
          </Typography>
        </Grid>
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <Grid size={3}>
          <Typography variant="body2" color="textSecondary">
            {t("pages.incidentPreviewPage.dateOfBirth")}
          </Typography>
          <Typography variant="subtitle1">
            {patientDetails.dateOfBirth
              ? ukLocalFormatDate(patientDetails.dateOfBirth)
              : "Unknown"}
          </Typography>
        </Grid>
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <Grid size={3}>
          <Typography variant="body2" color="textSecondary">
            {t("pages.incidentPreviewPage.address")}
          </Typography>
          <Typography variant="subtitle1">
            {patientDetails.address.address}
          </Typography>
        </Grid>
        <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
        <Grid size={1}>
          <Typography variant="body2" color="textSecondary">
            {t("pages.incidentPreviewPage.postcode")}
          </Typography>
          <Typography variant="subtitle1">
            {patientDetails.address.postcode}
          </Typography>
        </Grid>
      </Grid>
      <Divider orientation="horizontal" flexItem sx={{ my: 2 }} />
    </>
  );
};

const PlaceOfSafetyFollowUpQuestion = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  const { t } = useTranslation();
  return (
    <FormControl component="fieldset" sx={{ mt: "1.5rem" }}>
      <FormLabel component="legend">
        {t("pages.incidentPreviewPage.isCurrentPlaceOfSafetyLabel")}
      </FormLabel>
      <RadioGroup
        value={value}
        onChange={(e) => {
          onChange(e.target.value);
        }}
        name="isCurrentPlaceOfSafety"
      >
        <FormControlLabel
          value="yes"
          control={<Radio />}
          label={t("common.yes")}
        />
        <FormControlLabel
          value="no"
          control={<Radio />}
          label={t("common.no")}
        />
      </RadioGroup>
    </FormControl>
  );
};

const validationSchema = yup.object({
  placeOfSafety: yup.string().when("manualEntry", {
    is: (manualEntry: boolean) => manualEntry === true,
    then: () =>
      nonEmptyRequiredString.required(
        "Please enter your SWE registration number",
      ),
  }),
  manualPosName: yup.string().when("manualEntry", {
    is: (manualEntry: boolean) => manualEntry === true,
    then: () => nonEmptyRequiredString.required("name is required"),
  }),
  manualPosAddressData: yup.object().when("manualEntry", {
    is: (manualEntry: boolean) => manualEntry === true,
    then: () =>
      yup.object({
        address: nonEmptyRequiredString.required("address is required"),
        postalCode: nonEmptyRequiredString.required("postcode is required"),
      }),
  }),
});

function MCAIncidentPreview({
  incidentPreviewData,
}: {
  incidentPreviewData: PreviewVisionIncidentReponse;
}) {
  if (
    !("incidentType" in incidentPreviewData) ||
    incidentPreviewData.incidentType !== "mca"
  ) {
    throw new Error("Invalid incident type for MCAIncidentPreview");
  }

  const { t } = useTranslation();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const userContext = useContext(LoggedInUserContext);

  return (
    <>
      <PatientBanner
        isHidden
        patient={{
          id: v4(),
          name: {
            family: incidentPreviewData.patientDetails.name.family,
            given: incidentPreviewData.patientDetails.name.given,
          },
          dateOfBirth: incidentPreviewData.patientDetails.dateOfBirth,
          address: {
            address: incidentPreviewData.patientDetails.address.address,
            postalCode: incidentPreviewData.patientDetails.address.postcode,
          },
          deathNotificationStatus: null,
          restricted: false,
          version: 1,
          deceasedDateTime: null,
          registeredGpOdsCode: null,
          pdsOfficialDocumentGender: null,
          created: dayjs().toISOString(),
          updated: dayjs().toISOString(),
        }}
      />
      <Box sx={{ mb: 2 }}>
        <Banner
          body={t("pages.incidentPreviewPage.mcaIncidentBannerBody", {
            officerName: incidentPreviewData.officerDetails.name,
          })}
          bannerType={BannerList.WARNING}
        />
      </Box>
      <MultiPagePdf data={incidentPreviewData.incidentPdfBase64Data} />
      <FormFooterSection
        isSticky={true}
        isForcedSticky={true}
        saveLabel={"Accept"}
        discardLabel={"Decline"}
        disableDiscard={submitting}
        disableSubmit={submitting}
        onSave={async () => {
          setSubmitting(true);
          try {
            const res = await api.vision.acceptIncident({
              incidentId: incidentPreviewData.incidentId,
            });

            if (res.status !== 200) {
              renderErrorToast({
                message: t("pages.incidentPreviewPage.failedAcceptIncident"),
              });
            } else {
              renderSuccessToast({
                message: t("pages.incidentPreviewPage.incidentAccepted"),
              });
              logActionToDatadog("User accepted MCA incident", {
                incidentId: incidentPreviewData.incidentId,
              });
              navigate(
                routeFns.formContextPagePatientReconciliation(
                  res.data.formContextId,
                  res.data.patientId,
                ),
                { replace: true },
              );
            }
          } catch {
            renderErrorToast({
              message: t("pages.incidentPreviewPage.failedAcceptIncident"),
            });
          } finally {
            setSubmitting(false);
          }
        }}
        onCancel={async () => {
          setSubmitting(true);
          try {
            const res = await api.vision.rejectIncident(
              incidentPreviewData.incidentId,
              t("pages.incidentPreviewPage.receivedInError"),
            );
            if (res.status !== 204) {
              renderErrorToast({
                message: t("pages.incidentPreviewPage.failedRejectIncident"),
              });
            } else {
              renderSuccessToast({
                message: t("pages.incidentPreviewPage.incidentRejected"),
              });
              setTimeout(() => {
                if (isGuestUserSession(userContext?.user)) {
                  logout();
                }
                navigate(routeFns.home());
              }, 2000);
            }
          } catch {
            renderErrorToast({
              message: "Failed to reject incident",
            });
          } finally {
            setSubmitting(false);
          }
        }}
      />
    </>
  );
}

const defaultApi: VisionIncidentPreviewS136FormApi = {
  acceptIncident: (data) => api.vision.acceptIncident(data),
  rejectIncident: (incidentId, reason) =>
    api.vision.rejectIncident(incidentId, reason),
};

export function VisionIncidentPreviewS136Form({
  incidentPreviewData,
  incidentId,
  onAccepted,
  onRejected,
  isGuestUser = false,
  onLogout,
  apiOverride,
}: VisionIncidentPreviewS136FormProps) {
  const { t } = useTranslation();
  const effectiveApi = apiOverride ?? defaultApi;

  const activePlaceOfSafety = incidentPreviewData.placesOfSafety.find(
    (p) => p.isActive,
  );

  // Curated POS options for the typeahead. Sorted alphabetically by name so the
  // list is presented in a stable, predictable order, and augmented with a
  // combined "name, address" label so an option can be found and shown by
  // either its name or its address.
  const curatedPosOptions = useMemo(
    () =>
      [...(incidentPreviewData.curatedPlacesOfSafety ?? [])]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((pos) => ({ ...pos, combined: `${pos.name}, ${pos.address}` })),
    [incidentPreviewData.curatedPlacesOfSafety],
  );

  // Tracks the display text inside the typeahead input for POS selection.
  // Stored as component state (not Formik) because it represents transient
  // text-input state, while the Formik field stores only the confirmed ID.
  const [posTypeaheadInputValue, setPosTypeaheadInputValue] = useState("");

  const resetPosTypeahead = () => {
    setPosTypeaheadInputValue("");
  };

  const initialValues = {
    placeOfSafety: "",
    isCurrentPlaceOfSafety: "",
    selectedPlaceOfSafety: "",
    manualEntry: false,
    manualPosName: "",
    manualPosAddressData: {
      address: "",
      postalCode: "",
      isConfirmed: false,
    },
    trustId: "",
    trustName: "", // local UI state only - not sent to server
    trustOdsCode: "",
    trustIsNotListed: false,
  };

  return (
    <PopupDialog
      open
      title={t("pages.incidentPreviewPage.incidentPreviewTitle")}
      fullWidth
    >
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          let selectedPos:
            VisionAcceptIncidentRequest["placeOfSafety"] | undefined =
            undefined;

          // Did the user select an emha-managed place of safety?
          const selectedCuratedPos =
            incidentPreviewData.curatedPlacesOfSafety?.find(
              (pos) => pos.id === values.selectedPlaceOfSafety,
            );

          // Did this emha-managed place of safety exist on the incident already
          const matchingIncidentPos = selectedCuratedPos
            ? incidentPreviewData.placesOfSafety.find(
                (pos) =>
                  pos.eMHACuratedPlaceOfSafetyId === selectedCuratedPos.id,
              )
            : null;

          if (values.manualEntry) {
            // or was it manually entered
            // (no ods code or curated place of safety id)
            selectedPos = {
              // put a synthetic ID on here so we can match to it later.
              // It won't match up to a vision incident form ID
              id: v4(),
              name: values.manualPosName,
              address: values.manualPosAddressData.address,
              postalCode: values.manualPosAddressData.postalCode,
              trustId: values.trustId || undefined,
            };
          } else if (matchingIncidentPos) {
            selectedPos = {
              id: matchingIncidentPos.id,
              name: matchingIncidentPos.name,

              address: matchingIncidentPos.address,
              postalCode: matchingIncidentPos.postalCode,

              eMHACuratedPlaceOfSafetyId:
                matchingIncidentPos.eMHACuratedPlaceOfSafetyId,

              odsCode: matchingIncidentPos.odsCode,
              trustId:
                (selectedCuratedPos?.trustId ?? values.trustId) || undefined,
            };
          } else if (selectedCuratedPos) {
            selectedPos = {
              // put a synthetic ID on here so we can match to it later.
              // It won't match up to a vision incident form ID
              id: v4(),
              name: selectedCuratedPos.name,
              address: selectedCuratedPos.address,
              postalCode: selectedCuratedPos.postalCode,

              eMHACuratedPlaceOfSafetyId: selectedCuratedPos.id,
              odsCode: selectedCuratedPos.odsCode,
              trustId:
                (selectedCuratedPos.trustId ?? values.trustId) || undefined,
            };
          }
          // if none of the above then they just
          // accepted the currently active incident place of safety
          else if (activePlaceOfSafety) {
            // Find the curated POS that matches the active POS for trust info
            const activeCuratedPos =
              incidentPreviewData.curatedPlacesOfSafety?.find(
                (pos) =>
                  pos.id === activePlaceOfSafety.eMHACuratedPlaceOfSafetyId,
              );
            selectedPos = {
              id: activePlaceOfSafety.id,
              name: activePlaceOfSafety.name,
              address: activePlaceOfSafety.address,
              postalCode: activePlaceOfSafety.postalCode,
              eMHACuratedPlaceOfSafetyId:
                activePlaceOfSafety.eMHACuratedPlaceOfSafetyId,
              odsCode: activePlaceOfSafety.odsCode,
              trustId:
                (activeCuratedPos?.trustId ?? values.trustId) || undefined,
            };
          }

          if (!selectedPos) {
            renderErrorToast({
              message: t(
                "pages.incidentPreviewPage.selectPlaceOfSafetyMessage",
              ),
            });
            setSubmitting(false);
            return;
          }

          const isCurrentPlaceOfSafety =
            incidentPreviewData.section136MhaStatusCalculationEnabled &&
            values.isCurrentPlaceOfSafety !== ""
              ? values.isCurrentPlaceOfSafety === "yes"
              : undefined;

          setSubmitting(true);
          try {
            const res = await effectiveApi.acceptIncident({
              incidentId,
              placeOfSafety: selectedPos,
              ...(isCurrentPlaceOfSafety !== undefined
                ? { isCurrentPlaceOfSafety }
                : {}),
            });

            if (res.status !== 200) {
              renderErrorToast({
                message: t("pages.incidentPreviewPage.failedAcceptIncident"),
              });
            } else {
              logActionToDatadog(
                "User accepted incident - selected place of safety",
                {
                  incidentId,
                  placeOfSafety: selectedPos,
                  placeOfSafetyConfirmed: values.placeOfSafety,
                  isCurrentPlaceOfSafety,
                },
              );
              onAccepted(res.data.formContextId, res.data.patientId);
            }
          } catch {
            renderErrorToast({
              message: t("pages.incidentPreviewPage.failedAcceptIncident"),
            });
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {({
          values,
          isSubmitting,
          setSubmitting,
          setFieldValue,
          errors,
          touched,
          submitCount,
        }) => {
          // Determine if we need to show TrustPicker based on current selection
          const getSelectedPosTrustInfo = () => {
            if (values.placeOfSafety === "yes") {
              // Using the active place of safety from the incident
              const curatedPos =
                incidentPreviewData.curatedPlacesOfSafety?.find(
                  (pos) =>
                    pos.id === activePlaceOfSafety?.eMHACuratedPlaceOfSafetyId,
                );
              return {
                trustId: curatedPos?.trustId,
                trustName: curatedPos?.trustName ?? undefined,
              };
            }
            if (
              values.placeOfSafety === "no" &&
              !values.manualEntry &&
              values.selectedPlaceOfSafety
            ) {
              // Using a selected curated place of safety
              const curatedPos =
                incidentPreviewData.curatedPlacesOfSafety?.find(
                  (pos) => pos.id === values.selectedPlaceOfSafety,
                );
              return {
                trustId: curatedPos?.trustId,
                trustName: curatedPos?.trustName ?? undefined,
              };
            }
            // Manual entry or no selection - no trust info available
            return { trustId: undefined, trustName: undefined };
          };

          const selectedPosTrustInfo = getSelectedPosTrustInfo();
          const hasTrustFromPos = !!selectedPosTrustInfo.trustId;

          // A POS context is complete when:
          // - Manual entry is checked, OR
          // - User accepted the active POS ("yes"), OR
          // - User chose "no" AND selected a curated POS from the typeahead
          const hasCompletePosContext =
            values.manualEntry ||
            values.placeOfSafety === "yes" ||
            (values.placeOfSafety === "no" &&
              values.selectedPlaceOfSafety !== "");

          // Show TrustPicker only when we have a complete POS context
          // but that POS doesn't already carry trust info
          const shouldShowTrustPicker =
            hasCompletePosContext && !hasTrustFromPos;

          // The follow-up question refers to a concrete "this place of safety",
          // so it must only appear once the POS is actually identified — the
          // active POS accepted, a curated POS chosen, or the manual details
          // fully entered — not merely when "Manually enter" is ticked.
          const hasPlaceOfSafetyDetails =
            values.placeOfSafety === "yes" ||
            (values.placeOfSafety === "no" &&
              !values.manualEntry &&
              values.selectedPlaceOfSafety !== "") ||
            (values.manualEntry &&
              values.manualPosName.trim() !== "" &&
              values.manualPosAddressData.address.trim() !== "" &&
              values.manualPosAddressData.postalCode.trim() !== "" &&
              values.manualPosAddressData.isConfirmed);

          const isConfirmDisabled =
            values.placeOfSafety === "" ||
            (incidentPreviewData.section136MhaStatusCalculationEnabled &&
              values.isCurrentPlaceOfSafety === "") ||
            (values.placeOfSafety === "no" &&
              !values.manualEntry &&
              !values.selectedPlaceOfSafety) ||
            (values.manualEntry &&
              (!values.manualPosName.trim() ||
                !values.manualPosAddressData.address.trim() ||
                !values.manualPosAddressData.postalCode.trim() ||
                !values.manualPosAddressData.isConfirmed)) ||
            (shouldShowTrustPicker &&
              !values.trustId &&
              !values.trustIsNotListed);

          return (
            <Form>
              <Stack sx={{ margin: "0.5rem", display: "flex" }}>
                <FormTitle
                  useReducedTopPadding={true}
                  hasTitleBottomMargin={false}
                  titleText={t("pages.incidentPreviewPage.confirmDetails")}
                />
                <Typography variant="body1">
                  {t("pages.incidentPreviewPage.incidentOfficer", {
                    name: incidentPreviewData.officerDetails.name,
                  })}
                </Typography>
                <Box sx={{ mt: "2rem" }}>
                  <ProfileCard
                    patientDetails={incidentPreviewData.patientDetails}
                  />
                </Box>
                <FormControl component="fieldset">
                  <FormLabel component="legend">
                    {t("pages.incidentPreviewPage.formControlLabel")}
                  </FormLabel>
                  <Typography sx={{ fontWeight: "bold", mt: "1rem" }}>
                    {activePlaceOfSafety?.name}
                  </Typography>
                  <Typography sx={{ fontWeight: "bold", mb: "1rem" }}>
                    {activePlaceOfSafety?.address},{" "}
                    {activePlaceOfSafety?.postalCode}
                  </Typography>
                  <RadioGroup
                    value={values.placeOfSafety}
                    onChange={(e) => {
                      setFieldValue("placeOfSafety", e.target.value);
                      void setFieldValue("isCurrentPlaceOfSafety", "");
                      setFieldValue("selectedPlaceOfSafety", "");
                      setFieldValue("manualEntry", false);
                      setFieldValue("manualPosName", "");
                      setFieldValue("manualPosAddressData", {
                        address: "",
                        postalCode: "",
                        isConfirmed: false,
                      });
                      setFieldValue("trustId", "");
                      setFieldValue("trustName", "");
                      setFieldValue("trustOdsCode", "");
                      setFieldValue("trustIsNotListed", false);
                      resetPosTypeahead();
                    }}
                    name="placeOfSafety"
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio />}
                      label={t("common.yes")}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio />}
                      label={t("common.no")}
                    />
                  </RadioGroup>
                </FormControl>

                {/* Show typeahead and manual entry only if 'No' is selected */}
                <RevealSection in={values.placeOfSafety === "no"}>
                  <Divider sx={{ my: "1.5rem" }} />
                  <>
                    <FormControl fullWidth>
                      <Typeahead
                        name="selectedPlaceOfSafety"
                        label={t("pages.incidentPreviewPage.dropdownPosTitle")}
                        options={curatedPosOptions}
                        optionsKey="combined"
                        getOptionLabel={(option) =>
                          typeof option === "string" ? option : option.combined
                        }
                        inputValue={posTypeaheadInputValue}
                        onInputChange={(value) => {
                          // When in manual-entry mode the Typeahead is disabled.
                          // MUI Autocomplete may still fire synthetic
                          // onInputChange events while it syncs its internal
                          // value→inputValue state; ignore them to avoid
                          // resetting trust fields or triggering an infinite
                          // re-render loop.
                          if (values.manualEntry) return;

                          setPosTypeaheadInputValue(value);
                          // When the typed text no longer matches a known POS,
                          // clear the confirmed Formik selection so the user
                          // cannot submit a stale value. The displayed label is
                          // the "name, address" combined string, so match on it.
                          const match = curatedPosOptions.find(
                            (pos) => pos.combined === value,
                          );
                          if (!match) {
                            setFieldValue("selectedPlaceOfSafety", "");
                            setFieldValue("trustId", "");
                            setFieldValue("trustName", "");
                            setFieldValue("trustOdsCode", "");
                            setFieldValue("trustIsNotListed", false);
                            void setFieldValue("isCurrentPlaceOfSafety", "");
                          }
                        }}
                        onChange={(value) => {
                          if (value && typeof value !== "string") {
                            setFieldValue("selectedPlaceOfSafety", value.id);
                            setPosTypeaheadInputValue(value.combined);
                          } else {
                            setFieldValue("selectedPlaceOfSafety", "");
                          }
                          // Reset trust fields whenever the POS selection changes
                          setFieldValue("trustId", "");
                          setFieldValue("trustName", "");
                          setFieldValue("trustOdsCode", "");
                          setFieldValue("trustIsNotListed", false);
                          void setFieldValue("isCurrentPlaceOfSafety", "");
                        }}
                        disabled={values.manualEntry}
                        allowFreeText={false}
                        enableFuse={false}
                        deterministicSearch
                        slotProps={{
                          popper: {
                            modifiers: [
                              {
                                name: "flip",
                                options: { padding: 40 },
                              },
                            ],
                          },
                        }}
                        testId="pos-typeahead"
                      />
                    </FormControl>

                    {/* Checkbox to manually enter data */}
                    <FormControlLabel
                      sx={{ mt: "1rem" }}
                      control={
                        <Checkbox
                          checked={values.manualEntry}
                          onChange={(e) => {
                            setFieldValue("manualEntry", e.target.checked);
                            setFieldValue("manualPosName", "");
                            setFieldValue("manualPosAddressData", {
                              address: "",
                              postalCode: "",
                              isConfirmed: false,
                            });
                            setFieldValue("selectedPlaceOfSafety", "");
                            setFieldValue("trustId", "");
                            setFieldValue("trustName", "");
                            setFieldValue("trustOdsCode", "");
                            setFieldValue("trustIsNotListed", false);
                            void setFieldValue("isCurrentPlaceOfSafety", "");
                            resetPosTypeahead();
                          }}
                        />
                      }
                      label={t("pages.incidentPreviewPage.manuallyEnter")}
                    />

                    {/* Display text fields if "Manually enter" is checked */}
                    <RevealSection in={values.manualEntry}>
                      <Box sx={{ mt: "1rem" }}>
                        <TextField
                          name="manualPosName"
                          muiTextFieldLabel={t(
                            "pages.incidentPreviewPage.name",
                          )}
                          useFullWidth
                          value={values.manualPosName}
                          onChange={(value) => {
                            setFieldValue("manualPosName", value);
                          }}
                          errorMessage={
                            touched.manualPosName
                              ? (errors.manualPosName as string)
                              : undefined
                          }
                          showHelperText={
                            !!(touched.manualPosName && errors.manualPosName)
                          }
                        />
                        <Box sx={{ mt: "1rem" }}>
                          <AddressField
                            name="manualPosAddressData"
                            googleMapsApiKey={config.googleMapsApiKey}
                            addressCheckBoxLabel={t(
                              "pages.incidentPreviewPage.confirmAddress",
                            )}
                            hideConfirmation={false}
                            value={values.manualPosAddressData}
                            onChange={(newValue) => {
                              setFieldValue("manualPosAddressData", newValue);
                            }}
                            errors={
                              touched.manualPosAddressData
                                ? (errors.manualPosAddressData as {
                                    address?: string;
                                    postalCode?: string;
                                  })
                                : undefined
                            }
                          />
                        </Box>
                      </Box>
                    </RevealSection>
                  </>
                </RevealSection>

                {/* Trust section - only show once a POS context is established */}
                <RevealSection in={hasCompletePosContext}>
                  <Divider sx={{ my: "1.5rem" }} />
                  <Box>
                    {shouldShowTrustPicker ? (
                      <>
                        <FormLabel component="legend" sx={{ mb: "0.5rem" }}>
                          {t("pages.incidentPreviewPage.selectTrustTitle")}
                        </FormLabel>
                        <TrustPicker
                          value={
                            values.trustId || values.trustIsNotListed
                              ? {
                                  id: values.trustId || undefined,
                                  name: values.trustName,
                                  odsCode: values.trustOdsCode || undefined,
                                  isNotListed: values.trustIsNotListed,
                                }
                              : null
                          }
                          onChange={(trust: SelectedTrust) => {
                            if (trust) {
                              setFieldValue("trustId", trust.id ?? "");
                              setFieldValue("trustName", trust.name);
                              setFieldValue(
                                "trustOdsCode",
                                trust.odsCode ?? "",
                              );
                              setFieldValue(
                                "trustIsNotListed",
                                trust.isNotListed,
                              );
                            } else {
                              setFieldValue("trustId", "");
                              setFieldValue("trustName", "");
                              setFieldValue("trustOdsCode", "");
                              setFieldValue("trustIsNotListed", false);
                            }
                          }}
                          allowNotListed={true}
                          label={t("pages.incidentPreviewPage.trust")}
                          errorMessage={
                            submitCount > 0 &&
                            !values.trustId &&
                            !values.trustIsNotListed
                              ? t("pages.incidentPreviewPage.selectTrust")
                              : undefined
                          }
                        />
                        {values.trustIsNotListed &&
                          incidentPreviewData.featureFlags
                            ?.patientReconciliationEnabled && (
                            <Typography
                              variant="body2"
                              color="textSecondary"
                              sx={{ mt: 1 }}
                            >
                              {t(
                                "pages.incidentPreviewPage.trustNotListedReconciliationWarning",
                              )}
                            </Typography>
                          )}
                      </>
                    ) : (
                      <Box>
                        <Typography
                          variant="body2"
                          color="textSecondary"
                          sx={{ mb: "0.25rem" }}
                        >
                          {t("pages.incidentPreviewPage.trust")}
                        </Typography>
                        <Typography sx={{ fontWeight: "bold" }}>
                          {selectedPosTrustInfo.trustName}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                          {t("pages.incidentPreviewPage.trustAutoSelected")}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </RevealSection>

                {/* Warning banner - show when 'No' is selected */}
                <RevealSection in={values.placeOfSafety === "no"}>
                  <Box sx={{ mt: "2rem" }}>
                    <Banner
                      bannerType={BannerList.WARNING}
                      title={t("pages.incidentPreviewPage.warningBannerText")}
                    />
                  </Box>
                </RevealSection>

                {/* The follow-up about the patient is always the final question,
                    appearing once a place of safety has been established so the
                    question sits consistently at the end regardless of whether
                    the POS was prepopulated or entered manually. */}
                <RevealSection
                  in={
                    hasPlaceOfSafetyDetails &&
                    incidentPreviewData.section136MhaStatusCalculationEnabled
                  }
                >
                  <Divider sx={{ my: "1.5rem" }} />
                  <PlaceOfSafetyFollowUpQuestion
                    value={values.isCurrentPlaceOfSafety}
                    onChange={(value) =>
                      void setFieldValue("isCurrentPlaceOfSafety", value)
                    }
                  />
                </RevealSection>

                <Divider sx={{ my: "1.5rem" }} />
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                  }}
                >
                  <Button
                    variant="outlined"
                    onClick={() => {
                      const handleRejectIncident = async () => {
                        setSubmitting(true);
                        try {
                          const res = await effectiveApi.rejectIncident(
                            incidentId,
                            t("pages.incidentPreviewPage.receivedInError"),
                          );
                          if (res.status !== 204) {
                            renderErrorToast({
                              message: t(
                                "pages.incidentPreviewPage.failedRejectIncident",
                              ),
                            });
                          } else {
                            renderSuccessToast({
                              message: t(
                                "pages.incidentPreviewPage.incidentRejected",
                              ),
                            });
                            setTimeout(() => {
                              if (isGuestUser) {
                                onLogout?.();
                              }
                              onRejected();
                            }, 2000);
                          }
                        } catch {
                          renderErrorToast({
                            message: "Failed to reject incident",
                          });
                        } finally {
                          setSubmitting(false);
                        }
                      };
                      void handleRejectIncident();
                    }}
                    label={t("pages.incidentPreviewPage.receivedInError")}
                    testId="reject-incident-button"
                  />
                  <Button
                    type="submit"
                    label={t("buttonLabels.continue")}
                    disabled={isConfirmDisabled || isSubmitting}
                    testId="confirm-incident-button"
                  />
                </Box>
              </Stack>
            </Form>
          );
        }}
      </Formik>
    </PopupDialog>
  );
}

export function VisionIncidentPreviewPage() {
  const userContext = useContext(LoggedInUserContext);

  const { t } = useTranslation();
  const incidentId = new URLSearchParams(window.location.search).get(
    "incident-id",
  );

  const navigate = useNavigate();
  const [{ loading, data: incidentPreviewData, response }] =
    apiHooks.vision.previewIncident(incidentId ?? "");

  useEffect(() => {
    if (!incidentId) {
      renderErrorToast({
        message:
          "Incident ID not found. Please try to login through share code/email link again",
      });
      navigate(routeFns.home());
    }
  }, []);

  // if we have already accepted then redirect to the form context page
  useEffect(() => {
    if (incidentPreviewData && "formContextId" in incidentPreviewData) {
      navigate(
        routeFns.formContextPage(
          incidentPreviewData.formContextId,
          incidentPreviewData.patientId,
        ),
        { replace: true },
      );
    }
  }, [incidentPreviewData]);

  if (
    loading ||
    !incidentPreviewData ||
    !("patientDetails" in incidentPreviewData)
  ) {
    if (response && response.status !== 200) {
      return (
        <Banner
          bannerType={BannerList.ERROR}
          title={(response.data as any).reason ?? t("errors.unknownError")}
        />
      );
    }
    return <CircularProgress />;
  }

  if (incidentPreviewData.incidentType === "mca") {
    return <MCAIncidentPreview incidentPreviewData={incidentPreviewData} />;
  }

  return (
    <VisionIncidentPreviewS136Form
      incidentPreviewData={incidentPreviewData}
      incidentId={incidentId ?? ""}
      onAccepted={(formContextId, patientId) => {
        // Accepting the incident opens a place-of-safety detention, which owes
        // Section 132 information.
        queueS132Prompt(userContext?.user, formContextId, "section136");
        navigate(routeFns.formContextPage(formContextId, patientId), {
          replace: true,
        });
      }}
      onRejected={() => navigate(routeFns.home())}
      isGuestUser={isGuestUserSession(userContext?.user)}
      onLogout={logout}
    />
  );
}
